import { type Comment } from '@lightdash/common';
import { Button, Grid, Group, Skeleton, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { type Editor, type JSONContent } from '@tiptap/react';
import { useCallback, useState, type FC } from 'react';
import { LightdashUserAvatar } from '../../../components/Avatar';
import { useSearchSpaceAccess } from '../../../hooks/useSpaceAccess';
import useApp from '../../../providers/App/useApp';
import useDashboardContext from '../../../providers/Dashboard/useDashboardContext';
import { type SuggestionsItem } from '../types';
import { getNameInitials } from '../utils';
import styles from './CommentForm.module.css';
import { LazyCommentWithMentions } from './CommentWithMentions/LazyCommentWithMentions';

type Props = {
    userName: string;
    onSubmit: (
        text: string,
        html: string,
        mentions: string[],
    ) => Promise<string | undefined>;
    isSubmitting: boolean;
    onCancel?: () => void;
    mode?: 'reply' | 'new';
};

const parseMentions = (data: JSONContent): string[] => {
    const mentions = (data.content || []).flatMap(parseMentions);
    if (data.type === 'mention' && data.attrs?.id) {
        mentions.push(data.attrs.id);
    }

    const uniqueMentions = [...new Set(mentions)];

    return uniqueMentions;
};

export const CommentForm: FC<Props> = ({
    userName,
    onSubmit,
    isSubmitting,
    onCancel,
    mode = 'new',
}) => {
    const { user } = useApp();
    const projectUuid = useDashboardContext((c) => c.projectUuid);
    const spaceUuid = useDashboardContext((c) => c.dashboard?.spaceUuid);
    const searchSpaceAccess = useSearchSpaceAccess(projectUuid, spaceUuid);

    const fetchSuggestions = useCallback(
        async (query: string): Promise<SuggestionsItem[]> =>
            (await searchSpaceAccess(query)).map((share) => ({
                id: share.userUuid,
                label:
                    `${share.firstName} ${share.lastName}`.trim() ||
                    share.email,
                disabled: false,
            })),
        [searchSpaceAccess],
    );

    const [shouldClearEditor, setShouldClearEditor] = useState(false);
    const [editor, setEditor] = useState<Editor | null>(null);

    const commentForm = useForm<Pick<Comment, 'replyTo'>>({
        initialValues: {
            replyTo: '',
        },
    });

    const handleSubmit = commentForm.onSubmit(async () => {
        if (editor === null || editor.getText().trim() === '') return;

        await onSubmit(
            editor.getText(),
            editor.getHTML(),
            parseMentions(editor.getJSON()),
        );
        setShouldClearEditor(true);
    });

    return (
        <form onSubmit={handleSubmit}>
            <Stack gap="xs" mt="xs">
                <Grid columns={20}>
                    <Grid.Col span={2}>
                        <LightdashUserAvatar
                            size="sm"
                            userUuid={user.data?.userUuid}
                            avatarUrl={user.data?.avatarUrl}
                            avatarGradient={user.data?.avatarGradient}
                        >
                            {getNameInitials(userName)}
                        </LightdashUserAvatar>
                    </Grid.Col>
                    <Grid.Col
                        span={18}
                        miw={0}
                        // Walkthrough anchor: the typed step of
                        // create:DashboardComments, with a suggestion the
                        // learner can use as written. Only the new-comment
                        // editor: a reply form sits inert in the thread.
                        {...(mode === 'new'
                            ? {
                                  'data-tour-anchor': 'comment-editor',
                                  'data-tour-hint': 'Write your comment',
                                  'data-tour-input': 'true',
                                  'data-tour-suggest':
                                      'That January spike looks like the promo. Worth a closer look?',
                              }
                            : {})}
                    >
                        {projectUuid && spaceUuid ? (
                            <LazyCommentWithMentions
                                fetchSuggestions={fetchSuggestions}
                                shouldClearEditor={shouldClearEditor}
                                setShouldClearEditor={setShouldClearEditor}
                                onUpdate={setEditor}
                            />
                        ) : (
                            <Skeleton h={30} w="100%" />
                        )}
                    </Grid.Col>
                </Grid>
                <Group justify="flex-end" gap="xs">
                    {onCancel && (
                        <Button
                            className={styles.cancelButton}
                            variant="default"
                            size="compact-xs"
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                    )}

                    <Button
                        className={styles.submitButton}
                        loading={isSubmitting}
                        size="compact-xs"
                        type="submit"
                        // Walkthrough action for create:DashboardComments
                        // (posting a new comment, not a reply). See
                        // scripts/scope-tours.
                        {...(mode === 'new'
                            ? {
                                  'data-tour-scope': 'create:DashboardComments',
                                  'data-tour-step': '2',
                                  'data-tour-route':
                                      '/projects/:projectUuid/dashboards/:dashboardUuid/view',
                                  'data-tour-label': 'Click Add comment',
                                  'data-tour-title': 'Comment on a dashboard',
                                  'data-tour-interactive': 'true',
                                  'data-tour-via':
                                      '[data-tour-nav="browse"] >> [data-tour-nav="all-dashboards"] >> [data-tour-anchor="dashboard-row"][data-tour-value="Jaffle Shop overview"] >> [data-tour-anchor="tile-comments"] >> [data-tour-anchor="comment-editor"]',
                                  'data-tour-docs':
                                      'explore/dashboards/interact.mdx#comment-on-a-tile:li2',
                              }
                            : {})}
                    >
                        {mode === 'reply' ? 'Reply' : 'Add comment'}
                    </Button>
                </Group>
            </Stack>
        </form>
    );
};
